import * as React from 'react';
import { useContext } from 'react';

import type { ApolloError } from '@apollo/client';

import type { AdminPageHeader_MyPageHeaderFragment as MyPageHeaderType } from 'bundles/composable-page-header-admin/queries/__generated__/AdminMyPageHeaderQuery';

export type AdminMyPageHeaderContextType = {
  myPageHeader?: MyPageHeaderType;
  hasEnrolledPrograms?: boolean;
  // To be used to indicate to page header renderers that we may want to render a fallback header as the call to get the header data failed if this is defined.
  error?: ApolloError;
};

const AdminMyPageHeaderContext = React.createContext<AdminMyPageHeaderContextType | undefined>(undefined);

// Created to ensure the page header BFF and the backup made via old queries are consistent
export type UseAdminMyPageHeaderResult = {
  enrolledDegreePrograms?: MyPageHeaderType['enrolledDegreePrograms'];
  enrolledEnterprisePrograms?: MyPageHeaderType['enrolledEnterprisePrograms'];
  naptimeOrganizationsWithProgramEnrollments?: MyPageHeaderType['naptimeOrganizationsWithProgramEnrollments'];
  naptimeChosenOrganization?: MyPageHeaderType['naptimeChosenOrganization'];
  chosenEnterpriseProgram?: MyPageHeaderType['chosenEnterpriseProgram'];
  chosenDegree?: MyPageHeaderType['chosenDegree'];
  hasCheatingIncidents?: MyPageHeaderType['hasCheatingIncidents'];
  canViewEducatorAdminLink?: MyPageHeaderType['canViewEducatorAdminLink'];
  isCourseraPlusLearner?: MyPageHeaderType['isCourseraPlusLearner'];
  courseraPlusSubscriptionCycle?: MyPageHeaderType['courseraPlusSubscriptionCycle'];
  naptimeManagedThirdPartyOrganizations?: MyPageHeaderType['naptimeManagedThirdPartyOrganizations'];
  hasEnrolledPrograms?: boolean | null;
};

export const useAdminMyPageHeader = (): UseAdminMyPageHeaderResult => {
  const context = useContext(AdminMyPageHeaderContext);

  if (!context) {
    throw new Error('useAdminMyPageHeader must be used within an AdminMyPageHeaderContext.Provider');
  }

  const { myPageHeader } = context;

  const enrolledDegreePrograms = myPageHeader?.enrolledDegreePrograms;
  const enrolledEnterprisePrograms = myPageHeader?.enrolledEnterprisePrograms;
  const enrolledOrganizations = myPageHeader?.naptimeOrganizationsWithProgramEnrollments;

  const hasEnrolledDegreePrograms = enrolledDegreePrograms && enrolledDegreePrograms.length > 0;
  const hasEnrolledEnterprisePrograms = enrolledEnterprisePrograms && enrolledEnterprisePrograms.length > 0;
  const hasEnrolledOrganizations = enrolledOrganizations && enrolledOrganizations.length > 0;

  const hasEnrolledPrograms = hasEnrolledDegreePrograms || hasEnrolledEnterprisePrograms || hasEnrolledOrganizations;

  return {
    enrolledDegreePrograms,
    enrolledEnterprisePrograms,
    naptimeOrganizationsWithProgramEnrollments: enrolledOrganizations,
    naptimeChosenOrganization: myPageHeader?.naptimeChosenOrganization,
    chosenEnterpriseProgram: myPageHeader?.chosenEnterpriseProgram,
    chosenDegree: myPageHeader?.chosenDegree,
    hasCheatingIncidents: myPageHeader?.hasCheatingIncidents,
    canViewEducatorAdminLink: myPageHeader?.canViewEducatorAdminLink,
    isCourseraPlusLearner: myPageHeader?.isCourseraPlusLearner,
    courseraPlusSubscriptionCycle: myPageHeader?.courseraPlusSubscriptionCycle,
    naptimeManagedThirdPartyOrganizations: myPageHeader?.naptimeManagedThirdPartyOrganizations,
    hasEnrolledPrograms,
  };
};

export const useAdminMyPageHeaderError = () => {
  const context = useContext(AdminMyPageHeaderContext);

  return context?.error;
};

export default AdminMyPageHeaderContext;
